import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useEffect, useRef } from "react";
import { ethers } from "ethers";
import { submitBridgeQuote } from "../../utils/relayer-client";

const BRIDGE_QUOTE_DEBOUNCE_MS = AMOUNT_QUOTE_DEBOUNCE_MS;
const BRIDGE_QUOTE_TIMEOUT_MS = 15_000;
const BRIDGE_QUOTE_DUPLICATE_RETRY_MS = 900;
const BRIDGE_QUOTE_DUPLICATE_RETRIES = 2;
const DUPLICATE_IN_FLIGHT_IDEMPOTENCY_ERROR =
  "Duplicate in-flight Idempotency-Key";
const BRIDGE_QUOTE_TIMEOUT_MESSAGE =
  "Bridge quote timed out. Please retry or enter a smaller amount.";
const BRIDGE_QUOTE_EXPIRY_SAFETY_MS = 5_000;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

function isDuplicateBridgeQuoteError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return (
    message.includes(DUPLICATE_IN_FLIGHT_IDEMPOTENCY_ERROR) &&
    message.includes("relay:bridge:quote")
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException ||
    (typeof error === "object" && error !== null && "name" in error)
    ? (error as { name?: string }).name === "AbortError"
    : false;
}

export function buildBridgeQuoteIdempotencyKey(
  requestId: number | string,
  attempt: number,
): string {
  const timestamp = Date.now().toString(36);
  const entropy = Math.random().toString(36).slice(2, 10);
  return `bridge-quote-ui-${requestId}-${attempt}-${timestamp}-${entropy}`;
}

export function withBridgeQuoteMetadata(
  quote: any,
  requestKey: string,
  sourceAuthority: string,
) {
  const now = Date.now();
  const submitWindowSeconds = Number(
    quote?.submit_within_seconds ?? quote?.submitWithinSeconds ?? 30,
  );
  const submitWindowMs =
    Number.isFinite(submitWindowSeconds) && submitWindowSeconds > 0
      ? submitWindowSeconds * 1000
      : 30_000;
  const usableWindowMs = Math.max(
    5_000,
    submitWindowMs - BRIDGE_QUOTE_EXPIRY_SAFETY_MS,
  );
  return {
    ...quote,
    quote_request_key: requestKey,
    source_authority: sourceAuthority,
    sourceAuthority,
    quote_received_at_ms: now,
    quote_expires_at_ms: now + usableWindowMs,
  };
}

interface UseBaseBridgeQuoteParams {
  enabled?: boolean;
  requestKey: string;
  sourceChainId: number;
  sourceToken: { address: string; decimals: number };
  sourceAmount: string;
  destinationChainId: number;
  destinationToken: { address: string };
  destinationRecipient: string;
  sourceAuthority: string;
  destinationAuthority: string;
  destinationPrivateRecipient?: {
    recipientKey: string;
    recipientMpk: string;
    destinationInboxPublicKey: string;
    recipientHandle?: string;
    handleVersion?: number;
  } | null;
  setBridgeQuote: (value: any | null) => void;
  setBridgeQuoteLoading: (value: boolean) => void;
  setBridgeQuoteError: (value: string | null) => void;
}

export function useBaseBridgeQuote({
  enabled = true,
  requestKey,
  sourceChainId,
  sourceToken,
  sourceAmount,
  destinationChainId,
  destinationToken,
  destinationRecipient,
  sourceAuthority,
  destinationAuthority,
  destinationPrivateRecipient,
  setBridgeQuote,
  setBridgeQuoteLoading,
  setBridgeQuoteError,
}: UseBaseBridgeQuoteParams) {
  const requestIdRef = useRef(0);

  useEffect(() => {
    let active = true;
    const requestId = (requestIdRef.current += 1);
    const abortController = new AbortController();
    let debounceTimer: number | null = null;
    let timeoutTimer: number | null = null;

    const isCurrentRequest = () => active && requestIdRef.current === requestId;

    const resetQuote = () => {
      if (!isCurrentRequest()) return;
      setBridgeQuote(null);
      setBridgeQuoteError(null);
      setBridgeQuoteLoading(false);
    };

    const run = async () => {
      if (
        !enabled ||
        !requestKey ||
        !sourceAmount ||
        !destinationRecipient ||
        !sourceAuthority ||
        !destinationAuthority
      ) {
        resetQuote();
        return;
      }
      const parsed = parseFloat(sourceAmount);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        resetQuote();
        return;
      }

      setBridgeQuoteLoading(true);
      setBridgeQuote(null);
      setBridgeQuoteError(null);

      debounceTimer = window.setTimeout(async () => {
        if (!isCurrentRequest()) return;
        timeoutTimer = window.setTimeout(() => {
          abortController.abort();
        }, BRIDGE_QUOTE_TIMEOUT_MS);

        try {
          const amountBase = ethers
            .parseUnits(sourceAmount, sourceToken.decimals)
            .toString();
          let quote: any | null = null;
          for (
            let attempt = 0;
            attempt <= BRIDGE_QUOTE_DUPLICATE_RETRIES;
            attempt += 1
          ) {
            try {
              quote = await submitBridgeQuote({
                sourceChainId,
                sourceToken: sourceToken.address,
                sourceAmount: amountBase,
                sourceAmountIsNet: false,
                destinationChainId,
                destinationToken: destinationToken.address,
                destinationRecipient,
                sourceAuthority,
                destinationAuthority,
                prependOperatingExpenses: false,
                allowPartialFill: false,
                destinationPrivateRecipient:
                  destinationPrivateRecipient || undefined,
                idempotencyKey: buildBridgeQuoteIdempotencyKey(
                  requestId,
                  attempt,
                ),
                signal: abortController.signal,
              });
              break;
            } catch (err) {
              if (!isCurrentRequest()) return;
              if (
                attempt < BRIDGE_QUOTE_DUPLICATE_RETRIES &&
                isDuplicateBridgeQuoteError(err)
              ) {
                await delay(
                  BRIDGE_QUOTE_DUPLICATE_RETRY_MS,
                  abortController.signal,
                );
                continue;
              }
              throw err;
            }
          }

          if (!isCurrentRequest() || !quote) return;
          setBridgeQuote(
            withBridgeQuoteMetadata(quote, requestKey, sourceAuthority),
          );
        } catch (err: any) {
          if (!isCurrentRequest()) return;
          setBridgeQuote(null);
          setBridgeQuoteError(
            isAbortError(err)
              ? BRIDGE_QUOTE_TIMEOUT_MESSAGE
              : err?.message || "Bridge quote failed",
          );
        } finally {
          if (timeoutTimer) {
            window.clearTimeout(timeoutTimer);
            timeoutTimer = null;
          }
          if (isCurrentRequest()) setBridgeQuoteLoading(false);
        }
      }, BRIDGE_QUOTE_DEBOUNCE_MS);
    };

    void run();
    return () => {
      active = false;
      abortController.abort();
      if (debounceTimer) window.clearTimeout(debounceTimer);
      if (timeoutTimer) window.clearTimeout(timeoutTimer);
    };
  }, [
    enabled,
    requestKey,
    sourceChainId,
    sourceToken.address,
    sourceToken.decimals,
    sourceAmount,
    destinationChainId,
    destinationToken.address,
    destinationRecipient,
    sourceAuthority,
    destinationAuthority,
    destinationPrivateRecipient?.recipientKey,
    destinationPrivateRecipient?.recipientMpk,
    destinationPrivateRecipient?.destinationInboxPublicKey,
    destinationPrivateRecipient?.recipientHandle,
    destinationPrivateRecipient?.handleVersion,
    setBridgeQuote,
    setBridgeQuoteLoading,
    setBridgeQuoteError,
  ]);
}
